import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { sanitizeUrl } from '../../utils/functions.js';
import { getPresetSvg, STICKER_PACKS } from './presets.js';
import {
  PopupPortalRoot,
  StickerBox,
  StickerContent,
  StickerHandle,
  StickerLayerMenuItem,
  StickerLink,
  StickerOutline,
  StickerReplaceGrid,
  StickerReplaceItem,
  StickerReplaceTitle,
  StickerRotateHandle,
  StickerToolbar,
  StickerToolButton,
} from './stickerLayer.styled.js';

const round1 = (n) => Math.round(n * 10) / 10;
const stop = (e) => e.stopPropagation();

const LinkIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5' />
    <path d='M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5' />
  </svg>
);
const LayersIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinejoin='round'>
    <path d='M12 2 2 7l10 5 10-5z' />
    <path d='m2 17 10 5 10-5' />
    <path d='m2 12 10 5 10-5' />
  </svg>
);
const TrashIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M3 6h18M8 6V4h8v2m-2 0v13H10V6m-4 0 1 14h10l1-14' />
  </svg>
);
const SwapIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M4 8h13l-3-3m3 3-3 3M20 16H7l3-3m-3 3 3 3' />
  </svg>
);

// Keeps an open toolbar popup inside the profile card: after it opens, measure it
// against [data-site-container] and nudge it horizontally so an edge sticker's popup
// isn't clipped by the card's overflow:hidden. Returns [ref, shiftX] to apply as a
// translateX offset. Shared by the layers menu and the replace picker.
const useHorizontalClamp = (open, sig) => {
  const ref = useRef(null);
  const shiftRef = useRef(0);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open) {
      shiftRef.current = 0;
      setShift(0);
      return;
    }
    const el = ref.current;
    const bounds = el?.closest('[data-site-container]')?.getBoundingClientRect();
    if (!el || !bounds) return;
    const r = el.getBoundingClientRect();
    const pad = 8;
    // Strip the shift already applied so we clamp from the un-nudged baseline in one pass.
    const baseLeft = r.left - shiftRef.current;
    const baseRight = r.right - shiftRef.current;
    let next = 0;
    if (baseLeft < bounds.left + pad) next = bounds.left + pad - baseLeft;
    else if (baseRight > bounds.right - pad) next = bounds.right - pad - baseRight;
    shiftRef.current = next;
    setShift(next);
    // `sig` lets a caller re-clamp when the anchor moves (e.g. the toolbar while dragging).
  }, [open, sig]);
  return [ref, shift];
};

// A toolbar popup (replace picker / layers menu) rendered through a portal to
// document.body with fixed positioning. This lets it spill *outside* the phone
// preview frame (which clips its own overflow) and be clamped to the browser
// viewport instead — handy for the wide replace grid. Only ever used in the
// Dashboard editor: on the live site the toolbar isn't rendered at all.
const ToolbarPopup = ({ anchorRef, width, scroll, children }) => {
  const popupRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const p = popupRef.current?.getBoundingClientRect();
      const pw = p?.width || width || 200;
      const ph = p?.height || 0;
      const pad = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = a.left + a.width / 2 - pw / 2;
      left = Math.max(pad, Math.min(left, vw - pw - pad));
      let top = a.bottom + 6;
      // Flip above the button if it would fall off the bottom of the viewport.
      if (ph && top + ph > vh - pad) {
        const above = a.top - ph - 6;
        top = above >= pad ? above : Math.max(pad, vh - ph - pad);
      }
      setPos({ left, top });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef, width]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <PopupPortalRoot
      ref={popupRef}
      data-sticker-popup
      $scroll={!!scroll}
      onPointerDown={stop}
      style={{
        width,
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </PopupPortalRoot>,
    document.body,
  );
};

// Renders the sticker graphic. emoji = text glyph, preset = our inline SVG,
// image = an uploaded CDN url. dangerouslySetInnerHTML is safe here: preset SVGs
// are static strings we author in presets.js, never user input.
const StickerInner = ({ sticker }) => {
  // Centre the emoji glyph in a square box (flex, aspect-ratio 1). Rendering it as
  // plain line-height text leaves the glyph sitting low/off-centre inside the box,
  // so the selection outline (which wraps the box) looked misaligned with the emoji.
  if (sticker.kind === 'emoji')
    return (
      <span
        style={{
          display: 'flex',
          width: '100%',
          aspectRatio: '1',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {sticker.value}
      </span>
    );
  if (sticker.kind === 'image')
    return (
      <img src={sticker.value} alt='' draggable={false} style={{ display: 'block', width: '100%', height: 'auto' }} />
    );
  const svg = getPresetSvg(sticker.value);
  // aspect-ratio keeps the square preset SVG (height="100%") from collapsing inside an auto-height span.
  return svg ? (
    <span style={{ display: 'block', width: '100%', aspectRatio: '1' }} dangerouslySetInnerHTML={{ __html: svg }} />
  ) : null;
};

// One sticker. The transform handlers (move / resize / rotate), layer ordering and link
// editing only wire up when `editable` (Dashboard preview, sticker mode). On the live site
// `editable` is false: the sticker is inert unless it has a link, in which case it's a
// clickable anchor.
export const Sticker = ({ sticker, layerWidth, editable, selected, onSelect, onChange, onDelete, onLayer }) => {
  const { x, y, size, rotation, flipX, opacity, link, z } = sticker;
  const boxRef = useRef(null);
  const replaceBtnRef = useRef(null);
  const layersBtnRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  // Keep the toolbar itself inside the card (its popups portal out on their own).
  const [toolbarRef, toolbarShift] = useHorizontalClamp(selected, `${x}|${y}|${rotation}|${layerWidth}`);

  // Deselecting hides the toolbar; make sure its popups don't linger and re-open on reselect.
  useEffect(() => {
    if (!selected) {
      setMenuOpen(false);
      setReplaceOpen(false);
    }
  }, [selected]);

  const pxSize = layerWidth ? (layerWidth * size) / 100 : 0;

  const drag = (onMove) => {
    const move = (ev) => onMove(ev);
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const startMove = (e) => {
    if (!editable) return;
    e.stopPropagation();
    onSelect?.();
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = x;
    const origY = y;
    // Measure the layer width live at drag start (offsetParent === StickerLayerRoot).
    // The `layerWidth` prop can be 0/stale on the first interaction, which would lock
    // horizontal movement (dxPct === 0) while vertical still worked.
    const lw = boxRef.current?.offsetParent?.getBoundingClientRect().width || layerWidth || 0;
    drag((ev) => {
      const dxPct = lw ? ((ev.clientX - startX) / lw) * 100 : 0;
      const nx = Math.min(100, Math.max(0, origX + dxPct));
      const ny = Math.max(0, origY + (ev.clientY - startY));
      onChange?.({ x: round1(nx), y: Math.round(ny) });
    });
  };

  const center = () => {
    const box = boxRef.current?.getBoundingClientRect();
    return box ? { cx: box.left + box.width / 2, cy: box.top + box.height / 2 } : null;
  };

  const startResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const c = center();
    if (!c) return;
    const startDist = Math.hypot(e.clientX - c.cx, e.clientY - c.cy);
    const origSize = size;
    drag((ev) => {
      const dist = Math.hypot(ev.clientX - c.cx, ev.clientY - c.cy);
      const ratio = startDist ? dist / startDist : 1;
      const ns = Math.min(90, Math.max(4, origSize * ratio));
      onChange?.({ size: round1(ns) });
    });
  };

  const startRotate = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const c = center();
    if (!c) return;
    const angleAt = (ev) => (Math.atan2(ev.clientY - c.cy, ev.clientX - c.cx) * 180) / Math.PI;
    const startAngle = angleAt(e);
    const origRot = rotation;
    drag((ev) => {
      let nr = origRot + (angleAt(ev) - startAngle);
      nr = ((nr % 360) + 360) % 360;
      onChange?.({ rotation: Math.round(nr) });
    });
  };

  const handleLink = () => {
    const next = window.prompt('Link URL (leave empty to remove):', link || '');
    if (next === null) return;
    onChange?.({ link: next.trim() || null });
  };

  const layer = (direction) => {
    onLayer?.(direction);
    setMenuOpen(false);
  };

  // Swap the current sticker for another built-in preset (keeps id/position/size, only
  // changes what's drawn). Emoji/image stickers can be replaced with a preset this way too.
  const replaceWith = (value) => {
    onChange?.({ kind: 'preset', value });
    setReplaceOpen(false);
  };

  const content = (
    <StickerContent style={{ fontSize: pxSize ? `${pxSize}px` : '40px', transform: flipX ? 'scaleX(-1)' : 'none' }}>
      <StickerInner sticker={sticker} />
    </StickerContent>
  );

  return (
    <StickerBox
      ref={boxRef}
      data-sticker-id={sticker.id}
      $editable={editable}
      $linked={!editable && !!link}
      onPointerDown={startMove}
      style={{
        left: `${x}%`,
        top: `${y}px`,
        width: pxSize ? `${pxSize}px` : `${size}%`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        opacity,
        // A selected sticker jumps above all siblings so its outline, resize/rotate handles
        // and action toolbar are never covered by another (e.g. newer) sticker's box.
        zIndex: selected ? 9999 : z,
      }}
    >
      {!editable && link ? (
        <StickerLink href={sanitizeUrl(link)} target='_blank' rel='noopener noreferrer'>
          {content}
        </StickerLink>
      ) : (
        content
      )}

      {selected && (
        <>
          <StickerOutline />
          <StickerRotateHandle onPointerDown={startRotate} />
          <StickerHandle $pos='tl' onPointerDown={startResize} />
          <StickerHandle $pos='tr' onPointerDown={startResize} />
          <StickerHandle $pos='bl' onPointerDown={startResize} />
          <StickerHandle $pos='br' onPointerDown={startResize} />

          <StickerToolbar
            ref={toolbarRef}
            $rotation={rotation}
            onPointerDown={stop}
            style={{ transform: `translateX(calc(-50% + ${toolbarShift}px)) rotate(${-rotation}deg)` }}
          >
            <StickerToolButton
              type='button'
              title={link ? 'Edit link' : 'Add link'}
              $active={!!link}
              onPointerDown={stop}
              onClick={handleLink}
            >
              <LinkIcon />
            </StickerToolButton>

            <StickerToolButton
              ref={replaceBtnRef}
              type='button'
              title='Replace sticker'
              $active={replaceOpen}
              onPointerDown={stop}
              onClick={() => {
                setMenuOpen(false);
                setReplaceOpen((o) => !o);
              }}
            >
              <SwapIcon />
            </StickerToolButton>

            <StickerToolButton
              ref={layersBtnRef}
              type='button'
              title='Layer'
              $active={menuOpen}
              onPointerDown={stop}
              onClick={() => {
                setReplaceOpen(false);
                setMenuOpen((o) => !o);
              }}
            >
              <LayersIcon />
            </StickerToolButton>

            <StickerToolButton type='button' title='Delete' $danger onPointerDown={stop} onClick={() => onDelete?.()}>
              <TrashIcon />
            </StickerToolButton>
          </StickerToolbar>

          {replaceOpen && (
            <ToolbarPopup anchorRef={replaceBtnRef} width={216} scroll>
              {STICKER_PACKS.map((pack) => (
                <React.Fragment key={pack.key}>
                  <StickerReplaceTitle>{pack.name}</StickerReplaceTitle>
                  <StickerReplaceGrid>
                    {pack.stickers.map((st) => (
                      <StickerReplaceItem
                        key={st.value}
                        type='button'
                        title={st.key}
                        $active={sticker.kind === 'preset' && sticker.value === st.value}
                        onClick={() => replaceWith(st.value)}
                        dangerouslySetInnerHTML={{ __html: st.svg }}
                      />
                    ))}
                  </StickerReplaceGrid>
                </React.Fragment>
              ))}
            </ToolbarPopup>
          )}

          {menuOpen && (
            <ToolbarPopup anchorRef={layersBtnRef} width={168}>
              <StickerLayerMenuItem type='button' onClick={() => layer('front')}>
                Bring to front
              </StickerLayerMenuItem>
              <StickerLayerMenuItem type='button' onClick={() => layer('forward')}>
                Bring forward
              </StickerLayerMenuItem>
              <StickerLayerMenuItem type='button' onClick={() => layer('backward')}>
                Send backward
              </StickerLayerMenuItem>
              <StickerLayerMenuItem type='button' onClick={() => layer('back')}>
                Send to back
              </StickerLayerMenuItem>
            </ToolbarPopup>
          )}
        </>
      )}
    </StickerBox>
  );
};
